import { useState, useEffect, useCallback, RefObject } from 'react';
import { useScrollLock } from 'usehooks-ts';

interface UseHamburgerMenuResult {
  /**
   * Whether the menu is currently open
   */
  isOpen: boolean;

  /**
   * Function to toggle the menu open/closed
   */
  toggleMenu: () => void;

  /**
   * Function to open the menu
   */
  openMenu: () => void;

  /**
   * Function to close the menu
   */
  closeMenu: () => void;
}

// Helper function to get focusable elements
const getFocusableElements = (menuRef: RefObject<HTMLElement>) => {
  return menuRef.current
    ?.querySelector('.MenuMain')
    ?.querySelectorAll(
      'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
    );
};

/**
 * Hook to manage hamburger menu state and behavior with accessibility support
 *
 * @param options - Configuration options including menu reference
 * @returns Object with menu state and control functions
 *
 * @example
 * ```tsx
 * const menuRef = useRef<HTMLDivElement>(null);
 * const { isOpen, toggleMenu } = useHamburgerMenu({ menuRef });
 *
 * return (
 *   <div ref={menuRef}>
 *     <button onClick={toggleMenu}>
 *       {isOpen ? 'Close' : 'Open'} Menu
 *     </button>
 *     <nav className={isOpen ? 'menu-open' : ''}>
 *       Menu content
 *     </nav>
 *   </div>
 * );
 * ```
 */
export const useHamburgerMenu = (
  menuRef: RefObject<HTMLElement>,
): UseHamburgerMenuResult => {
  const [isOpen, setIsOpen] = useState(false);
  const { lock, unlock } = useScrollLock({
    autoLock: false,
    lockTarget: '#scrollable',
  });

  const openMenu = useCallback(() => {
    setIsOpen(true);
    lock();
    // Add mobile-menu-open class to body for global styling
    document.body.classList.add('mobile-menu-open');
  }, [lock]);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    unlock();
    // Remove mobile-menu-open class from body
    document.body.classList.remove('mobile-menu-open');
  }, [unlock]);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isOpen, openMenu, closeMenu]);

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, closeMenu]);

  // Handle accessibility for keyboard navigation
  useEffect(() => {
    if (!menuRef) return;

    const handleClickOutside = (event: MouseEvent | KeyboardEvent) => {
      const focusableElements = getFocusableElements(menuRef);
      if (
        isOpen &&
        (event?.target as HTMLElement)?.classList?.contains('hamburger')
      ) {
        if (event instanceof KeyboardEvent && event.key === 'Tab') {
          event.preventDefault();
          (focusableElements?.[0] as HTMLElement)?.focus();
        }
      }
    };

    const removeEventListener = () => {
      document.removeEventListener('keydown', handleClickOutside);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleClickOutside);
    } else {
      removeEventListener();
    }

    return () => {
      removeEventListener();
    };
  }, [isOpen, menuRef]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, []);

  return {
    isOpen,
    toggleMenu,
    openMenu,
    closeMenu,
  };
};
